import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import BookCover from '@/components/base/BookCover';
import { useCollection } from '@/hooks/useCollection';
import type { Book } from '@/lib/types';

export default function BooksPage() {
  const { rows: books, loading, error, reload } = useCollection<Book>('books', 'sort_order', true);
  const published = books.filter((b) => b.is_published !== false);

  useEffect(() => {
    document.title = 'Books by Apostle A. C. Oliver — TGMCI Publications';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-40 pb-20 overflow-hidden bg-background-950 text-center">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/60 via-background-950 to-background-950" />
          <div className="relative z-10 max-w-[800px] mx-auto px-4">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">TGMCI Publications</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Books That Transform Lives
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/60 text-base leading-relaxed">
              Spirit-led teaching from Apostle A. C. Oliver on faith, identity, healing, marriage, and deliverance.
              Browse the collection and get your copy today.
            </p>
          </div>
        </section>

        {/* Books grid */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            {loading ? (
              <p className="text-center text-foreground-400 py-16">
                <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading books...
              </p>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 text-sm mb-3">Could not load the books.</p>
                <button type="button" onClick={reload} className="btn-primary">Retry</button>
              </div>
            ) : published.length === 0 ? (
              <p className="text-center text-foreground-400 py-16">No books available yet.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {published.map((book) => (
                  <article
                    key={book.id}
                    className="group bg-white rounded-xl overflow-hidden border border-background-200 hover:border-primary-200 hover:-translate-y-1 transition-all flex flex-col"
                  >
                    <div className="h-72 overflow-hidden">
                      <BookCover
                        title={book.title}
                        subtitle={book.subtitle}
                        author={book.author}
                        image={book.cover_image}
                        className="group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h2 className="font-heading font-semibold text-lg text-foreground-900 leading-tight mb-1">
                        {book.title}
                      </h2>
                      {book.subtitle && (
                        <p className="text-accent-600 text-xs font-label font-medium mb-3">{book.subtitle}</p>
                      )}
                      <p className="text-foreground-500 text-sm leading-relaxed line-clamp-3 mb-5">{book.description}</p>
                      <div className="mt-auto flex items-center justify-between gap-3">
                        {book.price ? (
                          <span className="font-heading font-bold text-foreground-900">{book.price}</span>
                        ) : (
                          <span className="text-xs text-foreground-400">Available on Amazon</span>
                        )}
                        <a
                          href={book.buy_url || '#'}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="btn-accent whitespace-nowrap text-sm px-4 py-2.5"
                        >
                          <i className="ri-shopping-bag-3-line" aria-hidden="true" />
                          Buy Now
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary-700 text-center px-4">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">Grow in the Word</h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">
            Every book is written to strengthen your walk with God and equip you for a life of purpose.
          </p>
          <a
            href="https://www.amazon.com/stores/author/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="btn-accent"
          >
            <i className="ri-book-2-line" aria-hidden="true" />
            View All on Amazon
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}